import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef, type MutableRefObject } from "react";
import { MathUtils, OrthographicCamera, type Group } from "three";
import { DEFAULT_YAK_TRAILS_CONFIG, type GameSnapshot } from "@games-monorepo/yak-trails-core";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";
import { TerritoryMeshes } from "./TerritoryMeshes";
import { TrailRibbon } from "./TrailRibbon";
import { VoxelSkin } from "./VoxelSkin";
import { CaptureSparkles, PlayerElimination } from "./GameEffects";
import { PlayerLabel } from "./PlayerLabel";
import {
  advancePresentationMotion,
  cameraZoomForTerritory,
  createPresentationMotionState,
  type PresentationPose,
} from "./motion";

const FOLLOW_CAMERA = {
  position: [0, YAK_TRAILS_CLIENT_TUNING.camera.height, YAK_TRAILS_CLIENT_TUNING.camera.zOffset] as [number, number, number],
  zoom: YAK_TRAILS_CLIENT_TUNING.camera.initialZoom,
  near: YAK_TRAILS_CLIENT_TUNING.camera.near,
  far: YAK_TRAILS_CLIENT_TUNING.camera.far,
};

function CameraRig({ pose, territoryPercent }: {
  pose: MutableRefObject<PresentationPose>;
  territoryPercent: number;
}) {
  const { camera } = useThree();
  const follow = useRef({ x: pose.current.x, y: pose.current.y });
  const projectionInitialized = useRef(false);
  useFrame((_, delta) => {
    const targetX = pose.current.x + pose.current.headingX * YAK_TRAILS_CLIENT_TUNING.camera.headingLead;
    const targetY = pose.current.y + pose.current.headingY * YAK_TRAILS_CLIENT_TUNING.camera.headingLead;
    const distance = Math.hypot(targetX - follow.current.x, targetY - follow.current.y);
    const blend = distance > YAK_TRAILS_CLIENT_TUNING.camera.teleportDistance
      ? 1
      : 1 - Math.exp(-delta * YAK_TRAILS_CLIENT_TUNING.camera.followSmoothing);
    follow.current.x = MathUtils.lerp(follow.current.x, targetX, blend);
    follow.current.y = MathUtils.lerp(follow.current.y, targetY, blend);
    camera.position.set(
      follow.current.x,
      YAK_TRAILS_CLIENT_TUNING.camera.height,
      follow.current.y + YAK_TRAILS_CLIENT_TUNING.camera.zOffset,
    );
    camera.lookAt(follow.current.x, 0, follow.current.y);
    if (camera instanceof OrthographicCamera) {
      const desiredZoom = cameraZoomForTerritory(territoryPercent);
      camera.zoom = projectionInitialized.current
        ? MathUtils.lerp(
          camera.zoom,
          desiredZoom,
          1 - Math.exp(-delta * YAK_TRAILS_CLIENT_TUNING.camera.zoomSmoothing),
        )
        : desiredZoom;
      projectionInitialized.current = true;
      camera.updateProjectionMatrix();
    }
  }, -1);
  return null;
}

function PlayerModel({ player, snapshot, isSelf, steeringDirection, presentationPose, networkLatencyMs }: {
  player: GameSnapshot["players"][number];
  snapshot: GameSnapshot;
  isSelf: boolean;
  steeringDirection?: MutableRefObject<{ x: number; y: number } | null>;
  presentationPose?: MutableRefObject<PresentationPose>;
  networkLatencyMs: number;
}) {
  const group = useRef<Group>(null);
  const motion = useRef(createPresentationMotionState(player));
  const heading = Math.atan2(player.headingY, player.headingX);
  useFrame((_, delta) => {
    if (!group.current) return;
    advancePresentationMotion(motion.current, player, snapshot, {
      deltaSeconds: delta,
      isSelf,
      latencyMs: networkLatencyMs,
      steering: isSelf ? steeringDirection?.current : null,
    });
    group.current.position.x = motion.current.x;
    group.current.position.y = YAK_TRAILS_CLIENT_TUNING.player.elevation;
    group.current.position.z = motion.current.y;
    const leanBlend = 1 - Math.exp(-delta * YAK_TRAILS_CLIENT_TUNING.player.leanSmoothing);
    group.current.rotation.x = MathUtils.lerp(
      group.current.rotation.x,
      motion.current.headingY * YAK_TRAILS_CLIENT_TUNING.player.leanAmount,
      leanBlend,
    );
    group.current.rotation.z = MathUtils.lerp(
      group.current.rotation.z,
      -motion.current.headingX * YAK_TRAILS_CLIENT_TUNING.player.leanAmount,
      leanBlend,
    );
    if (presentationPose) {
      presentationPose.current.x = motion.current.x;
      presentationPose.current.y = motion.current.y;
      presentationPose.current.headingX = motion.current.headingX;
      presentationPose.current.headingY = motion.current.headingY;
    }
  }, -2);
  if (player.status !== "active") return null;
  return (
    <group ref={group} position={[player.x, YAK_TRAILS_CLIENT_TUNING.player.elevation, player.y]}>
      <mesh position={[0, YAK_TRAILS_CLIENT_TUNING.player.shadowOffsetY, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[
          YAK_TRAILS_CLIENT_TUNING.player.shadowRadius,
          YAK_TRAILS_CLIENT_TUNING.player.shadowSegments,
        ]} />
        <meshBasicMaterial
          color="#17333a"
          transparent
          opacity={YAK_TRAILS_CLIENT_TUNING.player.shadowOpacity}
          depthWrite={false}
        />
      </mesh>
      <VoxelSkin
        skinId={player.skinId}
        heading={heading}
        poseRef={motion}
        scale={isSelf ? YAK_TRAILS_CLIENT_TUNING.player.selfScale : YAK_TRAILS_CLIENT_TUNING.player.remoteScale}
      />
      <PlayerLabel label={isSelf ? "YOU" : player.alias} isSelf={isSelf} />
      {isSelf && (
        <mesh position={[0, YAK_TRAILS_CLIENT_TUNING.player.markerHeight, 0]}>
          <octahedronGeometry args={[YAK_TRAILS_CLIENT_TUNING.player.markerRadius, 0]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#ffffff"
            emissiveIntensity={YAK_TRAILS_CLIENT_TUNING.player.markerEmissiveIntensity}
          />
        </mesh>
      )}
    </group>
  );
}

function Scene({ snapshot, owners, playerId, cameraPlayerId, territoryWorkerUrl, captureBurstTick, quality, steeringDirection, networkLatencyMs }: {
  snapshot: GameSnapshot;
  owners: Array<string | null>;
  playerId: string;
  cameraPlayerId?: string;
  territoryWorkerUrl?: string;
  captureBurstTick: number;
  quality: "high" | "low";
  steeringDirection: MutableRefObject<{ x: number; y: number } | null>;
  networkLatencyMs: number;
}) {
  const self = snapshot.players.find((player) => player.id === playerId);
  const cameraPlayer = snapshot.players.find((player) => player.id === cameraPlayerId) ?? self;
  const selfPresentationPose = useRef<PresentationPose>({
    x: self?.x ?? 0,
    y: self?.y ?? 0,
    headingX: self?.headingX ?? 1,
    headingY: self?.headingY ?? 0,
  });
  const cameraPresentationPose = useRef<PresentationPose>({
    x: cameraPlayer?.x ?? self?.x ?? 0,
    y: cameraPlayer?.y ?? self?.y ?? 0,
    headingX: cameraPlayer?.headingX ?? self?.headingX ?? 1,
    headingY: cameraPlayer?.headingY ?? self?.headingY ?? 0,
  });
  const cameraPose = cameraPlayer?.id === playerId ? selfPresentationPose : cameraPresentationPose;
  const trails = useMemo(() => snapshot.trails.map((trail) => (
    <TrailRibbon
      key={trail.playerId}
      points={trail.points}
      color={trail.color}
      liveEndpoint={trail.playerId === playerId ? selfPresentationPose : undefined}
    />
  )), [playerId, snapshot.trails]);
  const reducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return (
    <>
      <color attach="background" args={[YAK_TRAILS_CLIENT_TUNING.arena.backgroundColor]} />
      <fog attach="fog" args={[
        YAK_TRAILS_CLIENT_TUNING.arena.backgroundColor,
        YAK_TRAILS_CLIENT_TUNING.arena.fogNear,
        YAK_TRAILS_CLIENT_TUNING.arena.fogFar,
      ]} />
      <hemisphereLight args={[
        YAK_TRAILS_CLIENT_TUNING.arena.skyLightColor,
        YAK_TRAILS_CLIENT_TUNING.arena.groundLightColor,
        YAK_TRAILS_CLIENT_TUNING.arena.hemisphereIntensity,
      ]} />
      <directionalLight
        position={[
          YAK_TRAILS_CLIENT_TUNING.arena.directionalX,
          YAK_TRAILS_CLIENT_TUNING.arena.directionalY,
          YAK_TRAILS_CLIENT_TUNING.arena.directionalZ,
        ]}
        intensity={YAK_TRAILS_CLIENT_TUNING.arena.directionalIntensity}
        castShadow
        shadow-mapSize-width={YAK_TRAILS_CLIENT_TUNING.arena.shadowMapSize}
        shadow-mapSize-height={YAK_TRAILS_CLIENT_TUNING.arena.shadowMapSize}
        shadow-camera-left={-YAK_TRAILS_CLIENT_TUNING.arena.shadowCameraExtent}
        shadow-camera-right={YAK_TRAILS_CLIENT_TUNING.arena.shadowCameraExtent}
        shadow-camera-top={YAK_TRAILS_CLIENT_TUNING.arena.shadowCameraExtent}
        shadow-camera-bottom={-YAK_TRAILS_CLIENT_TUNING.arena.shadowCameraExtent}
      />
      <mesh position={[0, YAK_TRAILS_CLIENT_TUNING.arena.lowerFloorY, 0]} receiveShadow>
        <cylinderGeometry args={[
          DEFAULT_YAK_TRAILS_CONFIG.world.radius + YAK_TRAILS_CLIENT_TUNING.arena.lowerFloorTopRadiusOffset,
          DEFAULT_YAK_TRAILS_CONFIG.world.radius + YAK_TRAILS_CLIENT_TUNING.arena.lowerFloorBottomRadiusOffset,
          YAK_TRAILS_CLIENT_TUNING.arena.lowerFloorDepth,
          YAK_TRAILS_CLIENT_TUNING.arena.cylinderSegments,
        ]} />
        <meshStandardMaterial
          color={YAK_TRAILS_CLIENT_TUNING.arena.lowerFloorColor}
          roughness={YAK_TRAILS_CLIENT_TUNING.arena.lowerFloorRoughness}
        />
      </mesh>
      <mesh position={[0, YAK_TRAILS_CLIENT_TUNING.arena.upperFloorY, 0]} receiveShadow>
        <cylinderGeometry args={[
          DEFAULT_YAK_TRAILS_CONFIG.world.radius + YAK_TRAILS_CLIENT_TUNING.arena.upperFloorRadiusOffset,
          DEFAULT_YAK_TRAILS_CONFIG.world.radius + YAK_TRAILS_CLIENT_TUNING.arena.upperFloorRadiusOffset,
          YAK_TRAILS_CLIENT_TUNING.arena.upperFloorDepth,
          YAK_TRAILS_CLIENT_TUNING.arena.cylinderSegments,
        ]} />
        <meshStandardMaterial
          color={YAK_TRAILS_CLIENT_TUNING.arena.upperFloorColor}
          roughness={YAK_TRAILS_CLIENT_TUNING.arena.upperFloorRoughness}
        />
      </mesh>
      <TerritoryMeshes owners={owners} players={snapshot.players} workerUrl={territoryWorkerUrl} />
      {trails}
      {snapshot.players.map((player) => (
        <PlayerModel
          key={player.id}
          player={player}
          snapshot={snapshot}
          isSelf={player.id === playerId}
          steeringDirection={steeringDirection}
          presentationPose={player.id === playerId
            ? selfPresentationPose
            : player.id === cameraPlayer?.id
              ? cameraPresentationPose
              : undefined}
          networkLatencyMs={networkLatencyMs}
        />
      ))}
      {!reducedMotion && captureBurstTick > 0 && self?.status === "active" && (
        <CaptureSparkles
          key={captureBurstTick}
          position={[self.x, YAK_TRAILS_CLIENT_TUNING.player.elevation - 0.14, self.y]}
          color={self.color}
          count={quality === "high"
            ? YAK_TRAILS_CLIENT_TUNING.effects.captureParticlesHigh
            : YAK_TRAILS_CLIENT_TUNING.effects.captureParticlesLow}
        />
      )}
      {!reducedMotion && snapshot.players.filter((player) => player.status === "dead").map((player) => (
        <PlayerElimination
          key={`${player.id}:${player.respawnAtMs ?? "eliminated"}`}
          position={[player.x, YAK_TRAILS_CLIENT_TUNING.player.elevation, player.y]}
          color={player.color}
          count={quality === "high"
            ? YAK_TRAILS_CLIENT_TUNING.effects.deathParticlesHigh
            : YAK_TRAILS_CLIENT_TUNING.effects.deathParticlesLow}
          heading={Math.atan2(player.headingY, player.headingX)}
          skinId={player.skinId}
        />
      ))}
      <CameraRig pose={cameraPose} territoryPercent={cameraPlayer?.territoryPercent ?? self?.territoryPercent ?? 0} />
    </>
  );
}

export function ArenaScene({
  snapshot,
  owners,
  playerId,
  cameraPlayerId,
  quality = "high",
  territoryWorkerUrl,
  captureBurstTick = 0,
  steeringDirection,
  networkLatencyMs = 0,
}: {
  snapshot: GameSnapshot;
  owners: Array<string | null>;
  playerId: string;
  cameraPlayerId?: string;
  quality?: "high" | "low";
  territoryWorkerUrl?: string;
  captureBurstTick?: number;
  steeringDirection: MutableRefObject<{ x: number; y: number } | null>;
  networkLatencyMs?: number;
}) {
  return (
    <Canvas
      className="yak-trails__canvas"
      shadows={quality === "high" ? "percentage" : false}
      dpr={quality === "high" ? YAK_TRAILS_CLIENT_TUNING.quality.highDpr : YAK_TRAILS_CLIENT_TUNING.quality.lowDpr}
      orthographic
      camera={FOLLOW_CAMERA}
      gl={{ antialias: quality === "high", powerPreference: "high-performance", alpha: false }}
      fallback={<div className="yak-trails__webgl-error">This device cannot open the 3D arena.</div>}
    >
      <Scene
        snapshot={snapshot}
        owners={owners}
        playerId={playerId}
        cameraPlayerId={cameraPlayerId}
        territoryWorkerUrl={territoryWorkerUrl}
        captureBurstTick={captureBurstTick}
        quality={quality}
        steeringDirection={steeringDirection}
        networkLatencyMs={networkLatencyMs}
      />
    </Canvas>
  );
}
